import { DocumentNode, gql } from '@apollo/client';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  HStack,
  Heading,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { Dinero } from 'dinero.js';
import * as React from 'react';

import { MembershipStatus } from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card';

import { AccordionMembershipCard_MembershipDataFragment } from './graphql-types/AccordionMembershipCard_MembershipDataFragment';

import { ImportantDateItem } from './components/ImportantDateItem';
import { MembershipActionButtons } from './components/MembershipActionButtons';
import { MembershipActionsMenu } from './components/MembershipActionsMenu';
import { MembershipTermDetailsHeader } from './components/MembershipTermDetailsHeader';
import { NoMembershipStatus } from './components/NoMembershipStatus';
import { PaymentAlertTag } from './components/PaymentAlertTag';
import { StatusAlertTag } from './components/StatusAlertTag';
import { TimelineHeader } from './components/TimelineHeader';

export const AccordionMembershipCard: React.FC<{
  membershipData: AccordionMembershipCard_MembershipDataFragment;
  userId?: string;
  organizationId?: string;
  userName?: string;
  displayName: string;
  creditBalance: Dinero<number>;
  organizationIsOwnerless?: boolean;
  shouldBeAdminView?: boolean;
  canCurrentUserManageOrganizationProfile?: boolean;
}> & { membershipDataFragment: DocumentNode } = ({
  membershipData,
  userId,
  organizationId,
  userName,
  displayName,
  creditBalance,
  organizationIsOwnerless,
  shouldBeAdminView,
  canCurrentUserManageOrganizationProfile,
}) => {
  const membershipDataRelevantTerms = membershipData.relevantMembershipTerms;

  return (
    <Card padding="6">
      <Stack spacing="4">
        <Stack direction="row" spacing="4" justifyContent="space-between">
          <Text textStyle="xlBodyStrong">Membership</Text>
          <MembershipActionsMenu
            userId={userId}
            organizationId={organizationId}
            membershipData={membershipData}
            organizationIsOwnerless={organizationIsOwnerless}
            shouldBeAdminView={!!shouldBeAdminView}
          />
        </Stack>

        {membershipDataRelevantTerms.length ? (
          membershipDataRelevantTerms.map((term, index) => {
            const membershipTermName = term.membershipType.name;

            const nextTermIndex = index + 1;
            const hasNextTerm =
              index < membershipDataRelevantTerms.length &&
              dayjs(term.endDate).isSameOrBefore(
                dayjs(membershipDataRelevantTerms[nextTermIndex]?.startDate)
              );

            const isExpandable = term.importantDates.length > 0;

            return (
              <Stack key={term._id} spacing="0">
                <TimelineHeader
                  startDate={term.startDate}
                  lifecycleStatus={term.lifecycleStatus}
                />

                <Accordion allowMultiple variant="baguette">
                  <AccordionItem>
                    <Heading as="h2">
                      <AccordionButton
                        cursor={isExpandable ? 'pointer' : 'default'}
                      >
                        <Box
                          as="span"
                          flex="1"
                          textAlign="left"
                          textStyle="bodyStrong"
                        >
                          {membershipTermName}
                        </Box>

                        <HStack spacing="6">
                          {/* Payment alerts have priority over status alerts */}
                          {term.paymentAlert ? (
                            <PaymentAlertTag paymentAlert={term.paymentAlert} />
                          ) : term.statusAlert ? (
                            <StatusAlertTag statusAlert={term.statusAlert} />
                          ) : null}

                          <AccordionIcon />
                        </HStack>
                      </AccordionButton>
                    </Heading>

                    <MembershipTermDetailsHeader
                      membershipTerm={term}
                      userId={userId}
                      organizationId={organizationId}
                      shouldBeAdminView={!!shouldBeAdminView}
                      canCurrentUserManageOrganizationProfile={
                        !!canCurrentUserManageOrganizationProfile
                      }
                      membershipType={term.membershipType}
                    />

                    {(term.importantDates.length > 0 ||
                      term.isMembershipEndless) && (
                      <AccordionPanel pb="0" pt="4">
                        <HStack position="relative" spacing="4">
                          <Divider
                            borderWidth="2px"
                            position="absolute"
                            orientation="vertical"
                          />
                          <VStack align="start" spacing="4" flex="1">
                            <>
                              {term.importantDates.map(
                                (importantDate, index) => {
                                  return (
                                    <ImportantDateItem
                                      key={`${index}-${importantDate.date}`}
                                      importantDate={importantDate}
                                      creditBalance={creditBalance}
                                      isMembershipEndless={
                                        term.isMembershipEndless
                                      }
                                      hasRelevantFailedPayment={
                                        !!term.paymentOrder
                                          ?.relevantFailedPayment
                                          ?.paymentInitiated
                                      }
                                    />
                                  );
                                }
                              )}

                              <MembershipActionButtons
                                membershipTerm={term}
                                displayName={displayName}
                                hasNextTerm={hasNextTerm}
                                shouldBeAdminView={!!shouldBeAdminView}
                                canCurrentUserManageOrganizationProfile={
                                  !!canCurrentUserManageOrganizationProfile
                                }
                              />
                            </>
                          </VStack>
                        </HStack>
                      </AccordionPanel>
                    )}
                  </AccordionItem>
                </Accordion>
              </Stack>
            );
          })
        ) : (
          <NoMembershipStatus
            name={userName || displayName}
            shouldBeAdminView={!!shouldBeAdminView}
          />
        )}

        {membershipData.membershipStatus !== MembershipStatus.NOT_A_MEMBER &&
          !!membershipData.memberSince && (
            <Text textStyle="compact" color="gray.500">
              Member since{' '}
              {dayjs(membershipData.memberSince).format('MMM D, YYYY')}
            </Text>
          )}
      </Stack>
    </Card>
  );
};

AccordionMembershipCard.membershipDataFragment = gql`
  fragment AccordionMembershipCard_MembershipDataFragment on MembershipData {
    memberSince
    membershipStatus
    relevantMembershipTerms(input: $relevantMembershipTermsInput) {
      _id
      membershipType {
        _id
        name
        membershipRenewalSettings {
          renewalPeriodInDays
        }
        allowRenewals
      }
      startDate
      endDate
      lifecycleStatus
      parentMembershipTerm {
        _id
        owner {
          ... on Organization {
            _id
            displayName
          }
        }
      }
      statusAlert
      paymentAlert
      isMembershipEndless
      importantDates {
        ...ImportantDateItem_MembershipTermImportantDateFragment
      }
      paymentOrder {
        _id
        relevantFailedPayment {
          paymentInitiated
        }
      }
      ...MembershipActionButtons_MembershipTermDataFragment
      ...MembershipTermDetailsHeader_MembershipTermDataFragment
    }
    ...MembershipActionsMenu_MembershipDataFragment
  }
  ${ImportantDateItem.importantDateFragment}
  ${MembershipActionButtons.membershipTermDataFragment}
  ${MembershipTermDetailsHeader.membershipTermDataFragment}
  ${MembershipActionsMenu.membershipDataFragment}
`;
